import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Target, Loader2, CheckCircle, Lock, 
  Send, Eye, AlertCircle, Lightbulb, Award
} from "lucide-react";

export const RapidSession1Page = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [formData, setFormData] = useState({
    top_3_decisions: "",
    delaying_decision: "",
    over_involved: "",
    results_not_matching_effort: "",
    heavier_than_should: ""
  });

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        // Pre-fill if session data exists
        if (data.session_1?.pre_session_response) {
          try {
            const savedData = JSON.parse(data.session_1.pre_session_response);
            setFormData(prev => ({ ...prev, ...savedData }));
          } catch {
            // Response might be plain text from old format
          }
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    } finally {
      setLoading(false);
    }
  };

  const isLocked = () => {
    if (!enrollment) return true;
    // Session 1 is unlocked after intake is complete
    return enrollment.status === "intake_pending";
  };

  const isCompleted = () => {
    return enrollment?.session_1?.completed === true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await authFetch(`${API}/rapid/session/1/pre-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ response: JSON.stringify(formData) })
      });

      if (response.ok) {
        toast.success("Pre-session reflection submitted successfully!");
        fetchEnrollment();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isLocked()) {
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="session-1-locked">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Lock className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Session 1 is Locked</h1>
          <p className="text-neutral-600 mb-6">Please complete your intake form first to unlock this session.</p>
          <Button onClick={() => navigate("/rapid/intake")} className="bg-emerald-600 hover:bg-emerald-700">
            Complete Intake
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-session-1">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/rapid")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-semibold text-neutral-900">Session 1: Review & Analyze</h1>
                {isCompleted() && <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>}
              </div>
              <p className="text-sm text-neutral-500">R.A.P.I.D.™ Business Recalibration</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Page Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
            <Target className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Review & Analyze</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            <strong>Objective:</strong> Identify the internal constraints and assumptions currently shaping your decisions and execution.
          </p>
        </div>

        {/* Section 1: Session Context */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-slate-50 border-b">
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg">What We&apos;re Doing in This Session</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-neutral-700 mb-4">
              In this session, we focus on how decisions are currently being made—not whether they are right or wrong.
              The goal is to surface invisible constraints that create friction, delay, or over-effort in execution.
            </p>
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
              <p className="font-medium text-emerald-800 mb-2">This session is:</p>
              <ul className="space-y-1 text-emerald-700">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Business-focused</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Forward-facing</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" /> Decision-driven</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Pre-Session Reflection Form */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-emerald-50 border-b border-emerald-100">
            <CardTitle className="text-lg">Decision & Execution Baseline</CardTitle>
            <CardDescription className="text-emerald-700 mt-1">
              Answer honestly and directly. There are no right answers—only clarity.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  1. What are the top 3 decisions currently consuming the most mental or emotional energy?
                </Label>
                <Textarea
                  value={formData.top_3_decisions}
                  onChange={(e) => updateField("top_3_decisions", e.target.value)}
                  placeholder="List your top 3 decisions..."
                  className="min-h-28"
                  required
                  disabled={isCompleted()}
                  data-testid="s1-top-decisions"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  2. Which decision are you most aware you&apos;re delaying?
                </Label>
                <Textarea
                  value={formData.delaying_decision}
                  onChange={(e) => updateField("delaying_decision", e.target.value)}
                  placeholder="Describe the decision you know you're putting off..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s1-delaying"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  3. Where in your business are you over-involved compared to what your role should require?
                </Label>
                <Textarea
                  value={formData.over_involved}
                  onChange={(e) => updateField("over_involved", e.target.value)}
                  placeholder="Identify areas where you're too hands-on..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s1-over-involved"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  4. Where are results not matching effort?
                </Label>
                <Textarea
                  value={formData.results_not_matching_effort}
                  onChange={(e) => updateField("results_not_matching_effort", e.target.value)}
                  placeholder="Describe where input doesn't equal output..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s1-results"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  5. What feels heavier or more complicated than it logically should?
                </Label>
                <Textarea
                  value={formData.heavier_than_should}
                  onChange={(e) => updateField("heavier_than_should", e.target.value)}
                  placeholder="Identify what's taking more energy than warranted..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s1-heavier"
                />
              </div>

              {!isCompleted() && (
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
                    disabled={submitting}
                    data-testid="s1-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit & Prepare for Session 1
                      </>
                    )}
                  </Button>
                </div>
              )}

              {enrollment?.session_1?.pre_session_response && !isCompleted() && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-medium">Pre-session reflection submitted</p>
                    <p className="text-blue-700 text-sm">Your consultant will review your responses and provide feedback during the session.</p>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Section 3: Constraint Map (Post-Session - Consultant Fills In) */}
        <Card className={`border-neutral-200 ${!isCompleted() ? "opacity-60" : ""}`}>
          <CardHeader className="bg-purple-50 border-b border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Your Decision Constraints</CardTitle>
                <CardDescription className="text-purple-700 mt-1">
                  These are the internal constraints currently shaping how decisions are made and executed.
                </CardDescription>
              </div>
              {!isCompleted() && <Lock className="w-5 h-5 text-purple-400" />}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isCompleted() && enrollment?.session_1?.outputs?.constraint_map ? (
              <div className="space-y-4">
                {enrollment.session_1.outputs.constraint_map.decision_bottlenecks && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Decision Bottlenecks</h4>
                    <p className="text-neutral-700 bg-neutral-50 p-3 rounded-lg">
                      {enrollment.session_1.outputs.constraint_map.decision_bottlenecks}
                    </p>
                  </div>
                )}
                {enrollment.session_1.outputs.constraint_map.behavioral_patterns && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Behavioral Patterns</h4>
                    <p className="text-neutral-700 bg-neutral-50 p-3 rounded-lg">
                      {enrollment.session_1.outputs.constraint_map.behavioral_patterns}
                    </p>
                  </div>
                )}
                {enrollment.session_1.outputs.constraint_map.execution_friction && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Execution Friction Points</h4>
                    <p className="text-neutral-700 bg-neutral-50 p-3 rounded-lg">
                      {enrollment.session_1.outputs.constraint_map.execution_friction}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will fill this in after your session.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Assumption Audit (Post-Session - Consultant Fills In) */}
        <Card className={`border-neutral-200 ${!isCompleted() ? "opacity-60" : ""}`}>
          <CardHeader className="bg-amber-50 border-b border-amber-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Operating Assumptions Identified</CardTitle>
                <CardDescription className="text-amber-700 mt-1">
                  These assumptions are not facts—they are internal rules guiding action.
                </CardDescription>
              </div>
              {!isCompleted() && <Lock className="w-5 h-5 text-amber-400" />}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isCompleted() && enrollment?.session_1?.outputs?.assumptions ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-2 font-medium text-neutral-900">Assumption</th>
                      <th className="pb-2 font-medium text-neutral-900">Where It Shows Up</th>
                      <th className="pb-2 font-medium text-neutral-900">How It Limits Execution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollment.session_1.outputs.assumptions.map((item, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="py-3 pr-4 text-neutral-700">{item.assumption}</td>
                        <td className="py-3 pr-4 text-neutral-700">{item.where_shows_up}</td>
                        <td className="py-3 text-neutral-700">{item.limits_execution}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will fill this in after your session.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Consultant Recommendations — visible after session complete */}
        {isCompleted() && enrollment?.session_1?.outputs?.consultant_recommendations && (
          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-emerald-900">Consultant Recommendations</CardTitle>
                  <CardDescription className="text-emerald-700">Strategic advice from your consultant based on Session 1</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="bg-white rounded-lg p-4 border border-emerald-100 whitespace-pre-wrap text-neutral-800 leading-relaxed">
                {enrollment.session_1.outputs.consultant_recommendations}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => navigate("/rapid")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          {isCompleted() && (
            <Button onClick={() => navigate("/rapid/session/2")} className="bg-emerald-600 hover:bg-emerald-700">
              Continue to Session 2
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RapidSession1Page;
